// ** React Imports
import { ChangeEvent, Fragment, MouseEvent, useState } from 'react'

// ** Next Imports
import Link from 'next/link'

// ** MUI Components
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MuiCard, { CardProps } from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import MuiFormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'

// ** Icons Imports
import EyeOffOutline from 'mdi-material-ui/EyeOffOutline'
import EyeOutline from 'mdi-material-ui/EyeOutline'

// ** Configs
import themeConfig from 'src/configs/themeConfig'

// ** Layout Import

// ** Demo Imports
import { FormHelperText } from '@mui/material'
import { setCookie } from 'cookies-next'
import Head from 'next/head'
import router from 'next/router'
import useLocalStorage from 'src/@core/hooks/useLocalStorage'
import UserApi from 'src/@core/utils/api/user-api'
import AppLogo from 'src/views/logos/AppLogo'
import validator from 'validator'

interface State {
  name: string
  last_name: string
  email: string
  password: string
  password_show: boolean
  privacy_accept: boolean
  name_error: boolean
  last_name_error: boolean
  email_error: boolean
  password_error: boolean
  name_error_message?: string
  last_name_error_message?: string
  email_error_message?: string
  password_error_message?: string
  registration_error: boolean
  registration_error_message?: string
}

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '28rem' }
}))

const LinkStyled = styled(Link)(({ theme }) => ({
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.primary.main
}))

const FormControlLabel = styled(MuiFormControlLabel)<FormControlLabelProps>(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(4),
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  }
}))

const RegisterPage = () => {
  // ** States
  const [values, setValues] = useState<State>({
    name: '',
    last_name: '',
    email: '',
    password: '',
    password_show: false,
    privacy_accept: false,
    name_error: false,
    last_name_error: false,
    email_error: false,
    password_error: false,
    registration_error: false
  })

  const [, setUser] = useLocalStorage('user')

  const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [prop]: event.target.value,
      [`${prop}_error`]: false,
      [`${prop}_error_message`]: '',
      registration_error: false
    })
  }
  const handleClickShowPassword = () => {
    setValues({ ...values, password_show: !values.password_show })
  }
  const handleClickAcceptPrivacy = () => {
    setValues({
      ...values,
      privacy_accept: !values.privacy_accept,
      registration_error: false
    })
  }
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const requiredFieldText = 'This field is required.'

  const doAccountRegistration = async () => {
    const errors: Partial<State> = {}

    if (!values.name) {
      errors.name_error = true
      errors.name_error_message = requiredFieldText
    }
    if (!values.last_name) {
      errors.last_name_error = true
      errors.last_name_error_message = requiredFieldText
    }
    if (!values.email || !validator.isEmail(values.email)) {
      errors.email_error = true
      errors.email_error_message = requiredFieldText
    }
    if (!values.password) {
      errors.password_error = true
      errors.password_error_message = requiredFieldText
    }

    const hasMissingFields = errors.name_error || errors.last_name_error || errors.email_error || errors.password_error
    if (hasMissingFields) {
      setValues({ ...values, ...errors })

      return
    } else {
      if (!values.privacy_accept) {
        setValues({
          ...values,
          registration_error: true,
          registration_error_message: 'You must accept the terms and conditions to create an account.'
        })

        return
      }
    }

    setValues({ ...values, registration_error: false })

    try {
      const response = await UserApi.register({
        name: values.name,
        lastName: values.last_name,
        email: values.email,
        password: values.password
      })
      if (response.token) {
        setCookie('token', response.token)
        setUser(response.user)
        router.push('/')

        return
      } else {
        setValues({
          ...values,
          registration_error: true,
          registration_error_message: 'There was an error in our server. Please, try again...'
        })
      }
    } catch (e: any) {
      setValues({
        ...values,
        registration_error: true,
        registration_error_message:
          e?.response?.data?.message === 'registration_fail'
            ? 'There is an account registered with this email'
            : 'There was an error in our server. Please, try again...'
      })
    }
  }

  return (
    <>
      <Head>
        <title key={'title'}>{`Account registration`}</title>
      </Head>
      <Box className='content-center'>
        <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Card sx={{ zIndex: 1 }}>
            <CardContent sx={{ padding: theme => `${theme.spacing(12, 9, 7)} !important` }}>
              <Box sx={{ mb: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AppLogo />
                <Typography
                  variant='h6'
                  sx={{
                    ml: 3,
                    lineHeight: 1,
                    fontSize: '2.5rem !important',
                    fontFamily: 'Island Moments'
                  }}
                >
                  {themeConfig.templateName}
                </Typography>
              </Box>
              <Box sx={{ mb: 6 }}>
                <Typography variant='h5' sx={{ fontWeight: 600, marginBottom: 1.5 }}>
                  Account Registration
                </Typography>
                <Typography variant='body2'>Create your account and start using {themeConfig.templateName}</Typography>
              </Box>
              <form noValidate autoComplete='off' onSubmit={e => e.preventDefault()}>
                <TextField
                  autoFocus
                  fullWidth
                  id='name'
                  label='First Name'
                  onChange={handleChange('name')}
                  error={values.name_error}
                  helperText={values.name_error_message}
                  sx={{ marginBottom: 4 }}
                />
                <TextField
                  fullWidth
                  id='last_name'
                  label='Last Name'
                  onChange={handleChange('last_name')}
                  error={values.last_name_error}
                  helperText={values.last_name_error_message}
                  sx={{ marginBottom: 4 }}
                />
                <TextField
                  fullWidth
                  type='email'
                  label='Email'
                  onChange={handleChange('email')}
                  error={values.email_error}
                  helperText={values.email_error_message}
                  sx={{ marginBottom: 4 }}
                />
                <FormControl fullWidth>
                  <InputLabel htmlFor='auth-register-password'>Password</InputLabel>
                  <OutlinedInput
                    label='Password'
                    value={values.password}
                    error={values.password_error}
                    id='auth-register-password'
                    onChange={handleChange('password')}
                    type={values.password_show ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          aria-label='toggle password visibility'
                        >
                          {values.password_show ? <EyeOutline fontSize='small' /> : <EyeOffOutline fontSize='small' />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {values.password_error && (
                    <FormHelperText error id='password-error'>
                      {values.password_error_message}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControlLabel
                  control={<Checkbox checked={values.privacy_accept} onClick={handleClickAcceptPrivacy} />}
                  label={
                    <Fragment>
                      <span>I agree to </span>
                      <LinkStyled href='/privacy/' target='_blank'>
                        privacy policy & terms
                      </LinkStyled>
                    </Fragment>
                  }
                />
                {values.registration_error && (
                  <FormHelperText error id='password-error'>
                    {values.registration_error_message}
                  </FormHelperText>
                )}
                <Button
                  fullWidth
                  size='large'
                  type='submit'
                  variant='contained'
                  onClick={doAccountRegistration}
                  sx={{ marginBottom: 7 }}
                >
                  Sign up
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Typography variant='body2' sx={{ marginRight: 2 }}>
                    Already have an account?
                  </Typography>
                  <Typography variant='body2'>
                    <LinkStyled href='/login/'>Sign in instead</LinkStyled>
                  </Typography>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  )
}

export default RegisterPage
